/**
 * One shape for "there is nothing here yet".
 *
 * Round 12 A6. Counted strictly, the administrator met three classes of
 * dead end over nine occurrences and the Resilience Lead five distinct
 * screens over six: a table header drawn over nothing, a page of zero
 * tiles, a sentence telling her to press a control that is not on her
 * page, and four registers reporting "No X match." to someone who had
 * not searched.
 *
 * The pattern that fixes all of them had already shipped, on /readiness:
 * say what is missing, where to go, and to come back. This is that shape,
 * so the next empty surface inherits it instead of inventing a sixth
 * paragraph.
 *
 * The CTA is OPTIONAL and the caller decides, because most of these
 * routes are admin-gated and the repeat-P1 of four rounds was an action
 * offered to a seat that cannot complete it. A reader who cannot do the
 * work gets the sentence naming who can, and no link into a refusal.
 */
import { Link } from "react-router-dom";
import { Arrow } from "shared/components/Arrow";

export interface EmptyStateProps {
  /** Distinguishes this empty state in a drive; keep the existing id. */
  testid: string;
  /** What is missing, and what puts it there. One sentence. */
  message: string;
  /** Where to go. Omit for a reader who cannot go there. */
  cta?: { label: string; to: string } | undefined;
  /**
   * `dark` for the two navy canvases (constellation, capability map);
   * `light` everywhere else.
   */
  tone?: "light" | "dark" | undefined;
  /** Extra classes on the wrapper -- positioning, not colour. */
  className?: string | undefined;
}

export function EmptyState({
  testid,
  message,
  cta,
  tone = "light",
  className = "",
}: EmptyStateProps): JSX.Element {
  const body = tone === "dark" ? "text-slate-300" : "text-slate-600";
  const link =
    tone === "dark"
      ? "text-brand-brass hover:underline"
      : "text-brand-brassDark hover:underline";
  return (
    <div data-testid={testid} className={`text-sm ${body} ${className}`}>
      <p className="max-w-2xl">{message}</p>
      {cta && (
        <Link
          to={cta.to}
          data-testid={`${testid}-cta`}
          className={`mt-2 inline-block text-sm font-semibold ${link}`}
        >
          {cta.label} <Arrow />
        </Link>
      )}
    </div>
  );
}
